/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { PbCcElementDto } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  falsyCellRenderer,
  rendererIconByName,
  ROW_DELETED_CLASS,
  ROW_DIRTY_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { NumberTypes, queryFormEditable, State } from '@gh/common-shared';
import {
  buildEditableNumberColumn,
  buildNumberColumn,
  getRowIdFromData,
  GridModel,
  GridOptionsExt,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getPbCcStructureId,
  pbStructureFieldState,
  validatePbStructureField,
} from '../../../resources/price-book.model';
import { PbCommandService } from '../pb-command.service';
import { PbDetailsState } from '../pb-details.state';

@Injectable()
export class PbDetailsStructureGridDefinitionService extends LayoutBasedGridDefinitionWithModel {
  constructor(private state: State<PbDetailsState>,
              private commands: PbCommandService) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getPbCcStructureId,
      validate: validatePbStructureField,
      getCellDef: pbStructureFieldState,
      command: (entity, name, value) => this.commands.structureSetField(name, value, getPbCcStructureId(entity)),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      getRowId: getRowIdFromData(getPbCcStructureId),
      rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
      rowClassRules: {
        [ROW_DELETED_CLASS]: ({ data }: { data: PbCcElementDto }) => data.isActive !== true,
        [ROW_DIRTY_CLASS]: ({ data }: { data: PbCcElementDto }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      'isActive': {
        cellRenderer: falsyCellRenderer(rendererIconByName('done')),
      },
      'isSalesTax': {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
      'cost': buildNumberColumn(NumberTypes.Price),
      'markupPct': {
        sortable: false,
        ...buildEditableNumberColumn(NumberTypes.Percents_2, this.model.getCellAccessor('markupPct')),
      },
      'roundUp': {
        sortable: false,
        ...buildEditableNumberColumn(NumberTypes.RoundUp, this.model.getCellAccessor('roundUp')),
      },
      'price': buildNumberColumn(NumberTypes.Price),
      'deletePct': {
        sortable: false,
        ...buildEditableNumberColumn(NumberTypes.Percents_2, this.model.getCellAccessor('deletePct')),
      },
    };
  }
}
